import * as React from 'react';
import {View} from 'react-native';

import ListItem from '/component/base/ListItem';
import PeopleListItem from '/component/base/PeopleListItem';
import PercentageProgressListItem from '/component/base/PercentageProgressListItem';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import {CoachInfoCard} from '/constant/TestIDs';
import {useTranslation} from '/provider/TranslationProvider';
import {generateComponentCallback} from '/util/component.util';

import {styles} from './EligibleForBonusReportContent.style';

interface OrderingEntities {
  clients: any[];
  currentCount: number;
  target: number;
}

interface RecordData {
  orderingEntities: OrderingEntities;
  fqv: number;
  newOrderingEntitiesCount: number;
  remainingEligibility: number;
}

interface EligibleForBonusReportContentProps {
  title: string;
  recordData: RecordData;
  orderingEntitiesName: string;
  fqvName: string;
}

export const EligibleForBonusReportContent: React.FC<
  EligibleForBonusReportContentProps
> = ({
  title = '',
  recordData = {
    orderingEntities: {clients: [], currentCount: 0, target: 0},
    fqv: 0,
    newOrderingEntitiesCount: 0,
    remainingEligibility: 0,
  },
  orderingEntitiesName = '',
  fqvName = '',
}) => {
  const {tt} = useTranslation('reportCardContent.eligibleForBonus');
  const {
    orderingEntities,
    fqv,
    newOrderingEntitiesCount,
    remainingEligibility,
  } = recordData;
  const {clients, currentCount, target} = orderingEntities;

  const oeCompleted = currentCount >= target;

  const renderedOrderingEntities = (
    <PeopleListItem
      style={{listItem: {base: styles.listItem}}}
      hasCheckbox
      isChecked={oeCompleted}
      peopleList={clients}
      currentCount={currentCount}
      targetCount={target}
      canExpand={false}
      label={
        <Text>{tt('label.newOrderingEntities', 'New Ordering Entities')}</Text>
      }
      isCoach={false}
      oneLineLayout
      testID={CoachInfoCard.oeContent}
    />
  );

  const bonusFqv = parseInt(String(fqv), 10);
  const fqvTarget = 1000;
  const fqvCompleted = bonusFqv >= fqvTarget;

  const renderedFqvProgress = (
    <PercentageProgressListItem
      style={{
        listItem: {base: styles.listItem},
        numericProgressText: styles.listItemText,
      }}
      hasCheckbox
      isChecked={fqvCompleted}
      label={<Text>{fqvName}</Text>}
      numerator={bonusFqv}
      denominator={fqvTarget}
      oneLineLayout
      testID={CoachInfoCard.fqvContent}
    />
  );

  const renderedBonusOrderingEntitiesCount = (
    <ListItem
      style={{base: styles.listItem}}
      label={<Text>{orderingEntitiesName}</Text>}
      suffix={<Text variant="button">{newOrderingEntitiesCount}</Text>}
      testID={CoachInfoCard.bonusOeContent}
    />
  );

  const renderedDaysRemaining = (
    <ListItem
      testID={CoachInfoCard.daysForEligibilityContent}
      style={{base: styles.listItem}}
      label={
        <Text>
          {tt('label.daysRemaining', 'Days Remaining for Eligibility')}
        </Text>
      }
      suffix={
        <Text variant="button">
          {tt(
            'daysRemaining',
            generateComponentCallback({
              component: (
                <>
                  {remainingEligibility}{' '}
                  {remainingEligibility === 1 ? 'day' : 'days'}
                </>
              ),
            }),
          )}
        </Text>
      }
    />
  );

  return (
    <View style={styles.base}>
      <View style={styles.header}>
        <Gutter>
          <Text variant="heading">{title}</Text>
        </Gutter>
      </View>
      <Gutter>
        {renderedOrderingEntities}
        {renderedFqvProgress}
        {renderedBonusOrderingEntitiesCount}
        {renderedDaysRemaining}
      </Gutter>
    </View>
  );
};
